import { EventType } from "@adora-so/types";
import { CUSTOM_COHORT_CHANGE_EVENT_TAG, } from "@adora/lib";
import { debugLog } from "./debug";
import { MutationThrottler } from "./mutation-throttler";
import { isWebpSupported } from "./webp-support";
export class Recorder {
    limitedCanvasRecording;
    canvasRecordingEnabled;
    org;
    rrwebRecorder;
    onEvent;
    startable = true;
    mutationThrottler;
    constructor(limitedCanvasRecording, canvasRecordingEnabled, org, rrwebRecorder) {
        this.limitedCanvasRecording = limitedCanvasRecording;
        this.canvasRecordingEnabled = canvasRecordingEnabled;
        this.org = org;
        this.rrwebRecorder = rrwebRecorder;
        debugLog("new recorder");
    }
    setOnEvent(onEvent) {
        this.onEvent = onEvent;
    }
    get args() {
        const customBlock = this.org.blockedCSSClasses.length > 0
            ? `|(${this.org.blockedCSSClasses.join(")|(")})`
            : "";
        const customMask = this.org.maskedCSSClasses && this.org.maskedCSSClasses?.length > 0
            ? `|(${this.org.maskedCSSClasses?.join(")|(")})`
            : "";
        // Determine optimal image format - use WebP if supported, otherwise JPEG
        const imageFormat = isWebpSupported()
            ? "image/webp"
            : this.limitedCanvasRecording
                ? "image/jpeg"
                : "image/png";
        return {
            emit: (event) => {
                if (!this.onEvent) {
                    throw new Error("onEvent must be assigned before starting recording");
                }
                if (event.type === EventType.FullSnapshot) {
                    this.mutationThrottler?.reset();
                }
                const throttledEvent = this.mutationThrottler
                    ? this.mutationThrottler.throttleMutations(event)
                    : event;
                if (!throttledEvent) {
                    return;
                }
                this.onEvent(throttledEvent);
            },
            userTriggeredOnInput: true,
            // block any adora-block classes.
            blockClass: new RegExp(`(adora-block)|(rr-block|fs-exclude|ph-no-capture)${customBlock}`),
            maskTextClass: new RegExp(`(adora-mask)|(rr-mask|fs-mask)${customMask}`),
            maskAllInputs: true,
            maskInputFn: (text, element) => {
                return shouldUnmask(this.org, element) ? text : "*".repeat(text.length);
            },
            maskTextFn: (text, element) => {
                // Anything that we SIP on we should not mask by default.
                // This should closely align with `getClosestInteractive`, but
                // not exactly as we don't always want inputs or what not.
                if (element &&
                    this.org.maskByDefault &&
                    element.closest(`button,a,div[role="button"],div[role*="menuitem"],input[type="button"],input[type="submit"],input[type="reset"]`)) {
                    return text;
                }
                return element && shouldUnmask(this.org, element)
                    ? text
                    : text.replace(/[\S]/g, "*");
            },
            maskTextSelector: this.org?.maskByDefault ? "*" : "",
            recordCanvas: this.canvasRecordingEnabled,
            sampling: this.canvasRecordingEnabled
                ? {
                    canvas: this.limitedCanvasRecording ? 1 : 2,
                }
                : undefined,
            dataURLOptions: this.canvasRecordingEnabled
                ? {
                    type: imageFormat,
                    quality: 0.6,
                }
                : {},
        };
    }
    recordChangeCustomCohort(payload) {
        debugLog("sending custom cohort change", payload);
        this.rrwebRecorder.addCustomEvent(CUSTOM_COHORT_CHANGE_EVENT_TAG, payload);
    }
    recordCLS(metric) {
        this.rrwebRecorder.addCustomEvent("CLS", metric);
    }
    recordFID(metric) {
        this.rrwebRecorder.addCustomEvent("FID", metric);
    }
    recordLCP(metric) {
        this.rrwebRecorder.addCustomEvent("LCP", metric);
    }
    recordVisibilityChange(state) {
        this.rrwebRecorder.addCustomEvent("Adora.VisibilityChange", {
            visibilityState: state,
        });
    }
    recordFocusChange(state) {
        this.rrwebRecorder.addCustomEvent("Adora.FocusChange", {
            focusState: state,
        });
    }
    get isRecording() {
        return this.rrwebRecorder.isRecording;
    }
    setRecording(on) {
        debugLog("setRecording", `requested status: ${on}`, `current status ${this.rrwebRecorder.isRecording}`);
        if (this.rrwebRecorder.isRecording === on) {
            return;
        }
        if (on) {
            this.startRecording();
            return;
        }
        this.stopRecording();
    }
    startRecording() {
        if (this.isRecording) {
            throw new Error("recording already in progress!");
        }
        if (!this.startable) {
            debugLog("recorder is not startable, returning");
            return;
        }
        this.mutationThrottler = new MutationThrottler(this.rrwebRecorder, {
            bucketSize: 100,
            refillRate: 10,
            onBlockedNode: (id, node) => {
                debugLog("mutation throttler blocked node", { id, node });
            },
        });
        this.rrwebRecorder.record(this.args);
    }
    stopRecording() {
        this.mutationThrottler?.stop();
        this.mutationThrottler = undefined;
        this.rrwebRecorder.stop();
    }
    stopFutureRecordings() {
        this.startable = false;
    }
}
function shouldUnmask(org, element) {
    if (element.classList.contains("adora-unmask")) {
        return true;
    }
    for (const selector of org.unmaskSelectors) {
        if (Array.from(document.querySelectorAll(selector)).find((el) => el === element)) {
            return true;
        }
    }
    return false;
}
